import { useEffect, useState } from "react";
import { ExternalLink, Ship, Search, Newspaper, Star } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Reveal } from "@/components/Reveal";
import { Input } from "@/components/ui/input";
import { apiClient, API } from "@/lib/api";
import { RESOURCE_LINKS, COMMON_VESSELS } from "@/constants/options";

const slugify = (name) =>
  name
    .toLowerCase()
    .replace(/[^a-z]+/g, "-")
    .replace(/^-+|-+$/g, "");

const TABS = [
  { id: "news", label: "News and Media" },
  { id: "links", label: "Useful Links" },
  { id: "tracker", label: "Live Vessel Tracker" },
];

export default function Resources() {
  const [activeTab, setActiveTab] = useState("news");
  const [articles, setArticles] = useState(null);
  const [vesselQuery, setVesselQuery] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);

  useEffect(() => {
    apiClient
      .get("/news")
      .then(({ data }) => setArticles(data))
      .catch(() => setArticles([]));
  }, []);

  const featuredArticle = articles?.find((a) => a.is_featured) || null;
  const regularArticles = articles ? articles.filter((a) => !a.is_featured) : [];

  const suggestions = vesselQuery.trim()
    ? COMMON_VESSELS.filter((v) =>
        v.toLowerCase().includes(vesselQuery.trim().toLowerCase())
      ).slice(0, 6)
    : [];

  const handleVesselSearch = (e) => {
    e.preventDefault();
    const query = vesselQuery.trim();
    if (!query) return;
    setShowSuggestions(false);
    const url = `https://www.marinetraffic.com/en/ais/index/search/all?keyword=${encodeURIComponent(query)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleSuggestionSelect = (name) => {
    setVesselQuery(name);
    setShowSuggestions(false);
  };

  return (
    <div className="bg-cream min-h-screen">
      <Navbar />

      <section className="pt-32 sm:pt-40 pb-10 bg-cream" data-testid="resources-hero">
        <div className="max-w-5xl mx-auto px-4 sm:px-8 lg:px-10">
          <Reveal>
            <p className="label-eyebrow text-gold-muted mb-3">Resources</p>
            <h1 className="font-display font-bold tracking-tight text-3xl sm:text-4xl text-ink">
              Trade Links &amp; Live Corridor Tracking
            </h1>
            <p className="mt-3 text-base text-ink-soft max-w-2xl">
              Media coverage of Ferguson's, official customs resources across our corridors, plus a
              live view of vessel traffic moving through them.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="pb-6 bg-cream" data-testid="resources-tabs-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-8 lg:px-10">
          <div
            className="flex gap-6 sm:gap-10 border-b border-ink/10 overflow-x-auto"
            data-testid="resources-tabs"
          >
            {TABS.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setActiveTab(t.id)}
                data-testid={`resources-tab-${t.id}`}
                className={`shrink-0 pb-3 text-sm sm:text-base font-display font-semibold whitespace-nowrap border-b-2 transition-colors ${
                  activeTab === t.id
                    ? "text-ink border-gold"
                    : "text-ink-soft border-transparent hover:text-ink"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {activeTab === "news" && (
        <section className="py-10 lg:py-14 bg-cream" data-testid="resources-panel-news">
          <div className="max-w-5xl mx-auto px-4 sm:px-8 lg:px-10">
            <Reveal>
              <div className="flex items-center gap-2.5 mb-2">
                <Newspaper size={20} className="text-gold-muted" />
                <h2 className="font-display font-semibold text-xl sm:text-2xl text-ink">
                  Ferguson's in the News — Industry Features and Media Coverage
                </h2>
              </div>
            </Reveal>

            {articles === null ? (
              <p className="text-sm text-ink-soft mt-8">Loading articles…</p>
            ) : articles.length === 0 ? (
              <p className="text-sm text-ink-soft mt-8" data-testid="news-empty-state">
                No articles yet. Check back soon.
              </p>
            ) : (
              <div className="mt-8 space-y-8">
                {featuredArticle && (
                  <Reveal>
                    <div
                      data-testid={`news-card-featured-${featuredArticle.id}`}
                      className="rounded-2xl border border-gold/30 bg-white shadow-md overflow-hidden sm:flex"
                    >
                      {featuredArticle.thumbnail_path && (
                        <img
                          src={`${API}/news/thumbnail/${featuredArticle.thumbnail_path}`}
                          alt={featuredArticle.headline}
                          data-testid={`news-thumbnail-${featuredArticle.id}`}
                          className="w-full sm:w-72 h-48 sm:h-auto object-cover shrink-0"
                        />
                      )}
                      <div className="p-6 sm:p-8 flex flex-col flex-1">
                        <span className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-gold-muted mb-2">
                          <Star size={11} fill="currentColor" /> Featured — {featuredArticle.publication_name}
                        </span>
                        <h3 className="font-display font-bold text-ink text-xl sm:text-2xl leading-snug">
                          {featuredArticle.headline}
                        </h3>
                        <p className="text-sm sm:text-base text-ink-soft mt-3 flex-1">{featuredArticle.excerpt}</p>
                        <div className="flex items-center justify-between mt-5 pt-4 border-t border-ink/10">
                          <span className="text-xs text-ink-faint">{featuredArticle.published_date}</span>
                          <a
                            href={featuredArticle.article_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`news-read-article-btn-${featuredArticle.id}`}
                            className="flex items-center gap-1.5 text-sm font-display font-semibold text-gold-muted hover:text-gold-hover transition-colors"
                          >
                            Read Article <ExternalLink size={14} />
                          </a>
                        </div>
                      </div>
                    </div>
                  </Reveal>
                )}

                {regularArticles.length > 0 && (
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                    {regularArticles.map((a, i) => (
                      <Reveal key={a.id} delay={i * 0.05}>
                        <div
                          data-testid={`news-card-${a.id}`}
                          className="h-full flex flex-col rounded-xl border border-ink/10 bg-white shadow-sm overflow-hidden hover:border-gold/50 hover:shadow-md transition-all"
                        >
                          {a.thumbnail_path && (
                            <img
                              src={`${API}/news/thumbnail/${a.thumbnail_path}`}
                              alt={a.headline}
                              data-testid={`news-thumbnail-${a.id}`}
                              className="w-full h-36 object-cover"
                            />
                          )}
                          <div className="p-5 sm:p-6 flex flex-col flex-1">
                            <span className="text-[10px] font-mono uppercase tracking-widest text-gold-muted">
                              {a.publication_name}
                            </span>
                            <h3 className="font-display font-semibold text-ink mt-2 leading-snug">
                              {a.headline}
                            </h3>
                            <p className="text-sm text-ink-soft mt-2 flex-1">{a.excerpt}</p>
                            <div className="flex items-center justify-between mt-4 pt-3 border-t border-ink/10">
                              <span className="text-xs text-ink-faint">{a.published_date}</span>
                              <a
                                href={a.article_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid={`news-read-article-btn-${a.id}`}
                                className="flex items-center gap-1 text-xs font-display font-semibold text-gold-muted hover:text-gold-hover transition-colors"
                              >
                                Read Article <ExternalLink size={12} />
                              </a>
                            </div>
                          </div>
                        </div>
                      </Reveal>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </section>
      )}

      {activeTab === "links" && (
        <section className="py-10 lg:py-14 bg-cream" data-testid="resources-panel-links">
          <div className="max-w-5xl mx-auto px-4 sm:px-8 lg:px-10">
            <Reveal>
              <h2 className="font-display font-semibold text-xl sm:text-2xl text-ink mb-8">
                Useful Links
              </h2>
            </Reveal>
            <div className="grid sm:grid-cols-2 gap-5">
              {RESOURCE_LINKS.map((link, i) => (
                <Reveal key={link.name} delay={i * 0.05}>
                  <a
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`resource-link-${slugify(link.name)}`}
                    className="group block h-full rounded-xl border border-ink/10 bg-white shadow-sm p-5 sm:p-6 hover:border-gold/50 hover:shadow-md transition-all"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <h3 className="font-display font-semibold text-ink">{link.name}</h3>
                      <ExternalLink size={15} className="text-gold-muted shrink-0 mt-1 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                    </div>
                    <p className="text-sm text-ink-soft mt-2">{link.desc}</p>
                  </a>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {activeTab === "tracker" && (
        <section className="py-10 lg:py-14 bg-cream" data-testid="resources-panel-tracker">
          <div className="max-w-5xl mx-auto px-4 sm:px-8 lg:px-10">
            <Reveal>
              <div className="flex items-center gap-2.5 mb-3">
                <Ship size={20} className="text-gold-muted" />
                <h2 className="font-display font-semibold text-xl sm:text-2xl text-ink">
                  Live Vessel Tracker — Active Trade Corridors
                </h2>
              </div>
              <p className="text-sm text-ink-soft mb-6 max-w-2xl">
                Real-time vessel movement across the Caribbean basin and our active trade lanes,
                powered by MarineTraffic's free public map.
              </p>

              <form
                onSubmit={handleVesselSearch}
                data-testid="vessel-tracker-search-form"
                className="rounded-xl border border-gold/20 bg-white shadow-sm p-5 sm:p-6 mb-6"
              >
                <label htmlFor="vessel-search-input" className="text-sm font-display font-semibold text-ink">
                  Track Your Vessel
                </label>
                <div className="mt-2 relative">
                  <div className="flex gap-2">
                    <Input
                      id="vessel-search-input"
                      value={vesselQuery}
                      onChange={(e) => {
                        setVesselQuery(e.target.value);
                        setShowSuggestions(true);
                      }}
                      onFocus={() => setShowSuggestions(true)}
                      onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
                      autoComplete="off"
                      placeholder="Enter vessel name or IMO number"
                      data-testid="vessel-search-input"
                      className="bg-cream border-ink/15 text-ink placeholder:text-ink-faint"
                    />
                    <button
                      type="submit"
                      disabled={!vesselQuery.trim()}
                      data-testid="vessel-search-submit-btn"
                      className="shrink-0 flex items-center gap-1.5 bg-gold hover:bg-gold-hover disabled:opacity-50 text-[#0A140F] font-display font-semibold text-sm px-5 rounded-md transition-colors"
                    >
                      <Search size={15} /> Track
                    </button>
                  </div>
                  {showSuggestions && suggestions.length > 0 && (
                    <ul
                      data-testid="vessel-suggestions-list"
                      className="absolute left-0 right-[76px] top-full mt-1.5 bg-white border border-ink/10 rounded-lg shadow-md overflow-hidden z-10"
                    >
                      {suggestions.map((name) => (
                        <li key={name}>
                          <button
                            type="button"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => handleSuggestionSelect(name)}
                            data-testid={`vessel-suggestion-item-${slugify(name)}`}
                            className="w-full text-left px-4 py-2 text-sm text-ink hover:bg-cream-alt transition-colors"
                          >
                            {name}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <p className="text-xs text-ink-faint mt-2">Powered by MarineTraffic public data.</p>
              </form>

              <div className="rounded-2xl border border-ink/10 overflow-hidden shadow-sm bg-white">
                <iframe
                  title="Live Vessel Tracker"
                  data-testid="marinetraffic-embed"
                  src="https://www.marinetraffic.com/en/ais/embed/zoom:5/centery:18.0/centerx:-75.0/maptype:0/shownames:false/mmsi:0/"
                  width="100%"
                  height="480"
                  style={{ border: 0 }}
                  loading="lazy"
                />
              </div>
              <a
                href="https://www.marinetraffic.com/en/ais/home/centerx:-75.0/centery:18.0/zoom:5"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="marinetraffic-fallback-link"
                className="inline-block mt-3 text-xs text-gold-muted hover:underline"
              >
                Map not loading? View live vessel traffic on MarineTraffic.com
              </a>
            </Reveal>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
}
